import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  MapPin,
  Star,
  Camera,
  Globe,
  Lock,
  Loader2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { CheckInService } from '../services/checkInService';
import { CheckInData, CheckInResult, LocationData } from '../types';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface CheckInModalProps {
  displayId: number;
  displayName?: string;
  isOpen: boolean;
  onClose: () => void;
  onCheckIn: (data?: CheckInData) => Promise<CheckInResult>;
}

export const CheckInModal: React.FC<CheckInModalProps> = ({
  displayId,
  displayName,
  isOpen,
  onClose,
  onCheckIn,
}) => {
  const [note, setNote] = useState('');
  const [rating, setRating] = useState<number>(0);
  const [isPublic, setIsPublic] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [location, setLocation] = useState<LocationData | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);

  // Get location when modal opens
  useEffect(() => {
    if (isOpen && !location) {
      getCurrentLocation();
    }
  }, [isOpen]);

  const getCurrentLocation = async () => {
    setLocationLoading(true);
    setLocationError(null);

    try {
      const loc = await CheckInService.getCurrentLocation();
      if (loc) {
        setLocation(loc);
      } else {
        setLocationError('Location access denied or unavailable');
      }
    } catch (error) {
      setLocationError('Failed to get location');
      console.error('Location error:', error);
    } finally {
      setLocationLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const checkInData: CheckInData = {
      note: note.trim() || undefined,
      rating: rating > 0 ? rating : undefined,
      is_public: isPublic,
    };

    try {
      const result = await onCheckIn(checkInData);
      if (result.success) {
        // Modal will be closed by parent component
        // Reset form for next time
        setNote('');
        setRating(0);
        setIsPublic(true);
      }
    } catch (error) {
      console.error('Check-in error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRatingClick = (value: number) => {
    setRating(rating === value ? 0 : value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Check In
          </DialogTitle>
          <DialogDescription>
            {displayName ? `Checking in to ${displayName}` : 'Check in to this display'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Location Status */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-medium">Location</span>
            </div>
            <div className="flex items-center gap-2">
              {locationLoading ? (
                <div className="flex items-center gap-1 text-blue-600">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span className="text-xs">Getting location...</span>
                </div>
              ) : location ? (
                <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Located
                </Badge>
              ) : (
                <div className="flex items-center gap-1">
                  <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {locationError || 'No location'}
                  </Badge>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={getCurrentLocation}
                    className="text-xs h-6 px-2"
                  >
                    Retry
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Rating */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Rate this display (optional)</Label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleRatingClick(value)}
                  className={cn(
                    'p-1 rounded transition-colors',
                    'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500'
                  )}
                >
                  <Star
                    className={cn(
                      'w-6 h-6 transition-colors',
                      rating >= value
                        ? 'fill-yellow-400 text-yellow-400'
                        : 'text-gray-300 hover:text-yellow-300'
                    )}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="note" className="text-sm font-medium">
              Add a note (optional)
            </Label>
            <Textarea
              id="note"
              placeholder="Share your thoughts about this display..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={500}
              className="resize-none"
            />
            <div className="text-xs text-gray-500 text-right">
              {note.length}/500
            </div>
          </div>

          {/* Privacy */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              {isPublic ? (
                <Globe className="w-4 h-4 text-blue-600" />
              ) : (
                <Lock className="w-4 h-4 text-gray-600" />
              )}
              <div>
                <div className="text-sm font-medium">
                  {isPublic ? 'Public check-in' : 'Private check-in'}
                </div>
                <div className="text-xs text-gray-600">
                  {isPublic
                    ? 'Others can see you visited this display'
                    : 'Only visible to you'
                  }
                </div>
              </div>
            </div>
            <Switch
              checked={isPublic}
              onCheckedChange={setIsPublic}
              aria-label="Make check-in public"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Checking in...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Check In
                </div>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};